import type { LogContext, LogHandledErrorConfig } from './types'

/**
 * Creates a logHandledError function with injected logger and optional Sentry.
 *
 * Usage:
 * ```ts
 * const logHandledError = createLogHandledError({
 *     logger: myLogger,
 *     getSentry: () => ({ client: Sentry, enabled: serverConfig.SENTRY_ENABLED }),
 * })
 * ```
 */
export function createLogHandledError(config: LogHandledErrorConfig) {
    return function logHandledError(error: unknown, context: LogContext = {}, sentryCapture = false) {
        const message = context.message || 'Handled error'
        const section = context.section || 'unknown'
        const feature = context.feature || 'unspecified'

        config.logger.error(`[${section}] : ${message}`, error, {
            feature,
            section,
            cause: error instanceof Error ? undefined : error,
            ...context,
        })

        if (!sentryCapture) return

        const sentryConfig = config.getSentry?.()
        if (!sentryConfig?.enabled) return

        const err = error instanceof Error ? error : new Error(message)

        sentryConfig.client.withScope((scope) => {
            scope.setTag('section', section)
            scope.setTag('feature', feature)
            scope.setExtra('context', context)
            if (!(error instanceof Error)) {
                scope.setExtra('cause', error)
            }
            sentryConfig.client.captureException(err)
        })
    }
}
